import { Tiptap, useReminderTextEditor } from "@app/components/TipTap";
import { currentUserAtom } from "@app/store/userStore";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FlexCol } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { useAtomValue } from "jotai";
import { ChangeEvent, type FC, useState } from "react";
import { SupplierUser } from "../../supplier/types";
import { useSendQuestionnaireEmailModalTranslations } from "./i18n";
import { UserSelect } from "./UserSelect";

export const SendQuestionnaireEmailModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSendClick: (
        senderName: string,
        emailSubject: string,
        emailText: string,
        emailHtml: string,
        isTest: boolean,
        userIds?: string[],
    ) => void;
    supplierUsers?: SupplierUser[];
    disabled?: boolean;
    disabledTooltip?: string;
}> = ({
    open,
    onOpenChange,
    onSendClick,
    supplierUsers,
    disabled,
    disabledTooltip,
}) => {
    const { data: currentUser } = useAtomValue(currentUserAtom);

    const editor = useReminderTextEditor("supplier");

    const { t, keys } = useSendQuestionnaireEmailModalTranslations();

    const userName = `${currentUser?.currentUser.firstName} ${currentUser?.currentUser.lastName}`;

    const [subject, setSubject] = useState<string>(t(keys.default_subject));
    const [senderName, setSenderName] = useState<string>(userName);

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
        supplierUsers?.map((user) => user.id) || [],
    );

    const handleSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSubject(e.target.value);
    };
    const handleSenderNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSenderName(e.target.value);
    };

    const handleConfirmClick = (isTest: boolean) => {
        return () => {
            if (
                !subject ||
                !senderName ||
                (!isTest && supplierUsers && !selectedUserIds.length)
            ) {
                return;
            }
            onSendClick(
                senderName,
                subject,
                editor?.getText() || "",
                editor?.getHTML() || "",
                isTest,
                supplierUsers && !isTest ? selectedUserIds : undefined,
            );
            if (isTest) {
                return;
            }
            onOpenChange(false);
        };
    };

    return (
        <Modal open={open} onOpenChange={onOpenChange}>
            <Modal.Overlay scrollable>
                <Modal.Content>
                    <Modal.Header withClose>
                        <Modal.Title>{t(keys.modal_title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <Textfield
                                value={senderName}
                                onChange={handleSenderNameChange}
                                error={
                                    !senderName
                                        ? t(keys.error_cannot_be_empty)
                                        : undefined
                                }
                                placeholder={t(keys.sender_name_placeholder)}
                                hint={t(keys.sender_name_hint)}
                            />
                            <Textfield
                                value={subject}
                                onChange={handleSubjectChange}
                                error={
                                    !subject
                                        ? t(keys.error_cannot_be_empty)
                                        : undefined
                                }
                                placeholder={t(keys.subject_placeholder)}
                                hint={t(keys.subject_hint)}
                            />
                            <FlexCol>
                                <FlexCol
                                    p="4"
                                    br="lg"
                                    className="border border-teritiary"
                                >
                                    <Tiptap editor={editor} supplier />
                                </FlexCol>
                                <Text variant="body-sm" color="primary">
                                    {t(keys.email_content_hint)}
                                </Text>
                            </FlexCol>
                            {supplierUsers && (
                                <UserSelect
                                    supplierUsers={supplierUsers}
                                    selectedUserIds={selectedUserIds}
                                    setSelectedUserIds={setSelectedUserIds}
                                />
                            )}
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>
                            {t(keys.cancel_button)}
                        </Modal.CancelCTA>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Modal.ConfirmCTA
                                    variant="outlined"
                                    preventClose
                                    onClick={handleConfirmClick(true)}
                                >
                                    {t(keys.test_button)}
                                </Modal.ConfirmCTA>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.test_button_tooltip)}
                            </Tooltip.Content>
                        </Tooltip>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <div>
                                    <Modal.ConfirmCTA
                                        preventClose
                                        disabled={disabled}
                                        onClick={handleConfirmClick(false)}
                                    >
                                        {t(keys.send_button)}
                                    </Modal.ConfirmCTA>
                                </div>
                            </Tooltip.Trigger>
                            {disabled && (
                                <Tooltip.Content>
                                    {disabledTooltip}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
